/**
 * Shared CSS Classes
 * 
 * Centralized Tailwind CSS class strings to reduce code duplication
 * and maintain consistent styling across the application
 */

// ============================================================================
// LOADING SPINNERS
// ============================================================================

export const SPINNER_CLASSES = {
  // Small spinners (h-3 w-3)
  SMALL: 'animate-spin rounded-full h-3 w-3 border-b-2',
  SMALL_PRIMARY: 'animate-spin rounded-full h-3 w-3 border-b-2 border-primary-400',
  SMALL_WHITE: 'animate-spin rounded-full h-3 w-3 border-b-2 border-white',
  SMALL_YELLOW: 'animate-spin rounded-full h-3 w-3 border border-yellow-300 border-t-transparent',
  
  // Medium spinners (h-4 w-4)
  MEDIUM: 'animate-spin rounded-full h-4 w-4 border-b-2',
  MEDIUM_PRIMARY: 'animate-spin rounded-full h-4 w-4 border-b-2 border-primary-400',
  MEDIUM_WHITE: 'animate-spin rounded-full h-4 w-4 border-b-2 border-white',
  MEDIUM_CURRENT: 'animate-spin rounded-full h-4 w-4 border-b-2 border-current',
  
  // Large spinners (h-6 w-6)
  LARGE: 'animate-spin rounded-full h-6 w-6 border-b-2',
  LARGE_PRIMARY: 'animate-spin rounded-full h-6 w-6 border-b-2 border-primary-400',
  LARGE_WHITE: 'animate-spin rounded-full h-6 w-6 border-b-2 border-white',
  
  // Extra large spinners (h-8 w-8)
  XLARGE: 'animate-spin rounded-full h-8 w-8 border-b-2',
  XLARGE_PRIMARY: 'animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400',
  XLARGE_WHITE: 'animate-spin rounded-full h-8 w-8 border-b-2 border-white',
};

// ============================================================================
// MODAL CONTAINERS
// ============================================================================

export const MODAL_CLASSES = {
  // Modal overlays
  OVERLAY: 'fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4',
  
  // Modal containers
  CONTAINER_BASE: 'relative w-full bg-dark-800 shadow-2xl border border-dark-700',
  CONTAINER_SM: 'relative w-full max-w-md bg-dark-800 rounded-2xl shadow-2xl border border-dark-700 overflow-hidden',
  CONTAINER_MD: 'relative w-full max-w-lg bg-dark-800 rounded-xl shadow-2xl border border-dark-700 max-h-[90vh] overflow-y-auto',
  CONTAINER_LG: 'relative w-full max-w-2xl bg-dark-800 rounded-2xl shadow-2xl border border-dark-700 overflow-hidden',
  CONTAINER_SCROLLABLE: 'relative w-full max-w-md max-h-[90vh] bg-dark-800 rounded-2xl shadow-2xl border border-dark-700 overflow-hidden',
  
  // Close buttons
  CLOSE_BUTTON: 'absolute top-3 right-3 z-10 p-1.5 text-gray-400 hover:text-white transition-colors disabled:opacity-50',
};

// ============================================================================
// BUTTONS
// ============================================================================

export const BUTTON_CLASSES = {
  // Base button
  BASE: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
  
  // Primary buttons
  PRIMARY: 'bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg transition-colors',
  PRIMARY_SM: 'bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors',
  
  // Secondary buttons
  SECONDARY: 'bg-dark-700 hover:bg-dark-600 text-gray-300 hover:text-white border border-dark-600',
  
  // Danger/Delete buttons
  DANGER: 'bg-red-600 hover:bg-red-700 text-white',
  DANGER_GHOST: 'text-red-400 hover:text-red-300 hover:bg-red-600/10 rounded-lg transition-colors',
  
  // States
  DISABLED: 'disabled:opacity-50 disabled:cursor-not-allowed',
  LOADING: 'relative disabled:opacity-50 disabled:cursor-not-allowed',
};

// ============================================================================
// FORM INPUTS
// ============================================================================

export const INPUT_CLASSES = {
  BASE: 'w-full px-4 py-2.5 rounded-lg bg-dark-700 text-white border border-dark-600 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all',
  ERROR: 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
  LABEL: 'block text-sm font-medium text-gray-300',
  ERROR_MESSAGE: 'text-red-400 text-xs mt-1',
};

// ============================================================================
// ALERT/MESSAGE BOXES
// ============================================================================

export const ALERT_CLASSES = {
  // Success alerts
  SUCCESS: 'p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm',
  SUCCESS_LARGE: 'p-4 rounded-lg bg-green-500/10 border border-green-500/20',
  
  // Error alerts
  ERROR: 'p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm',
  ERROR_LARGE: 'p-4 rounded-lg bg-red-500/10 border border-red-500/20',
  
  // Warning alerts
  WARNING: 'p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm',
  WARNING_LARGE: 'bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3',
  
  // Info alerts
  INFO: 'p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm',
  INFO_LARGE: 'bg-dark-700/30 rounded-lg p-4',
};

// ============================================================================
// ICONS & BADGES
// ============================================================================

export const ICON_CLASSES = {
  // Icon containers
  CONTAINER_SM: 'w-8 h-8 rounded-lg flex items-center justify-center',
  CONTAINER_MD: 'w-10 h-10 rounded-xl flex items-center justify-center',
  CONTAINER_LG: 'w-12 h-12 rounded-xl flex items-center justify-center',
  
  // Gradient backgrounds
  PRIMARY_GRADIENT: 'bg-gradient-to-br from-primary-500 to-primary-700',
  ORANGE_GRADIENT: 'bg-gradient-to-br from-orange-500 to-orange-700',
  GREEN_GRADIENT: 'bg-gradient-to-br from-green-500 to-green-700',
  RED_GRADIENT: 'bg-gradient-to-br from-red-500 to-red-700',
  
  // Icon sizes
  XS: 'w-3 h-3',
  SM: 'w-4 h-4',
  MD: 'w-5 h-5',
  LG: 'w-6 h-6',
  XL: 'w-8 h-8',
};

// ============================================================================
// CARDS & CONTAINERS
// ============================================================================

export const CARD_CLASSES = {
  BASE: 'bg-dark-800 rounded-lg border border-dark-700 p-4',
  HOVER: 'bg-dark-800 rounded-lg border border-dark-700 p-4 hover:border-primary-500/50 transition-colors',
  GLASS: 'bg-dark-800/50 backdrop-blur-sm rounded-xl border border-dark-700',
};

// ============================================================================
// TEXT STYLES
// ============================================================================

export const TEXT_CLASSES = {
  HEADING_LG: 'text-2xl font-bold text-white',
  HEADING_MD: 'text-xl font-bold text-white',
  HEADING_SM: 'text-lg font-semibold text-white',
  
  BODY: 'text-gray-400 text-sm',
  BODY_MD: 'text-gray-300 text-sm',
  
  LABEL: 'text-sm font-medium text-gray-300',
  MUTED: 'text-gray-500 text-xs',
};

// ============================================================================
// UTILITY CLASSES
// ============================================================================

export const UTILITY_CLASSES = {
  // Flex layouts
  FLEX_CENTER: 'flex items-center justify-center',
  FLEX_BETWEEN: 'flex items-center justify-between',
  FLEX_START: 'flex items-center justify-start',
  
  // Spacing
  SPACE_Y_SM: 'space-y-2',
  SPACE_Y_MD: 'space-y-4',
  SPACE_Y_LG: 'space-y-6',
  
  SPACE_X_SM: 'space-x-2',
  SPACE_X_MD: 'space-x-4',
  
  // Transitions
  TRANSITION_ALL: 'transition-all duration-200',
  TRANSITION_COLORS: 'transition-colors duration-200',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Combine multiple class strings
 * @param {...string} classes - Class strings to combine
 * @returns {string} Combined class string
 */
export function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Get spinner class based on size and color
 * @param {string} size - 'small' | 'medium' | 'large' | 'xlarge'
 * @param {string} color - 'primary' | 'white' | 'current'
 * @returns {string} Spinner class string
 */
export function getSpinnerClass(size = 'medium', color = 'primary') {
  const sizeMap = {
    small: 'SMALL',
    medium: 'MEDIUM',
    large: 'LARGE',
    xlarge: 'XLARGE',
  };
  
  const key = `${sizeMap[size]}_${color.toUpperCase()}`;
  return SPINNER_CLASSES[key] || SPINNER_CLASSES.MEDIUM_PRIMARY;
}

/**
 * Get button class with variants
 * @param {string} variant - 'primary' | 'secondary' | 'danger'
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {boolean} disabled - Whether button is disabled
 * @returns {string} Button class string
 */
export function getButtonClass(variant = 'primary', size = 'md', disabled = false) {
  const classes = [BUTTON_CLASSES.BASE];
  
  // Variant
  if (variant === 'primary') {
    classes.push(size === 'sm' ? BUTTON_CLASSES.PRIMARY_SM : BUTTON_CLASSES.PRIMARY);
  } else if (variant === 'secondary') {
    classes.push(BUTTON_CLASSES.SECONDARY);
  } else if (variant === 'danger') {
    classes.push(BUTTON_CLASSES.DANGER);
  }
  
  // Disabled state
  if (disabled) {
    classes.push(BUTTON_CLASSES.DISABLED);
  }
  
  return classes.join(' ');
}
